import { useDispatch, useSelector } from "react-redux";
import DataTableComponent from "../../shared/components/data-table/data-table.component";
import { useForm } from "antd/lib/form/Form";
import { Row, Col, Modal, Button, Space, Drawer } from "antd";
import GenericFormComponent from "../../shared/components/generic-form/generic-form.component";
import { useCallback, useEffect, useMemo, useState } from "react";
import { RootState } from "../../shared/types/common";
import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  PlusOutlined,
  ApartmentOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { BiFilterAlt } from "react-icons/bi";
import CreateEditSubDepartmentComponent from "./components/create-edit-sub-deartment.component";
import {
  deleteSubDepartment,
  getSubDepartmentList,
} from "../../core/store/reducers/sub-department.slice";
import { TABLE_ROWS_COUNT } from "../../shared/constants";
import ActivityLogComponent from "../../shared/components/activity-log/activity-log.component";
import { accessAllowed, showNotification } from "../../shared/utils/utils";
import { allPermissions } from "../../shared/constants/constants";
import { ActiveFilterChipsBar } from "../../shared/components/active-filter-chips/active-filter-chips.component";
import { isFilterValueActive } from "../../shared/utils/active-filters";

const SUBDEPT_FILTER_LABELS: Record<string, string> = {
  subDepartmentName: "Name",
  status: "Status",
};

function subDeptFilterValueText(key: string, value: any): string {
  switch (key) {
    case "subDepartmentName":
      return String(value).trim();
    case "status":
      return Array.isArray(value) ? value.join(", ") : String(value);
    default:
      return String(value);
  }
}

const SUBDEPT_FILTER_CLEAR: Record<string, any> = {
  subDepartmentName: undefined,
  status: [],
};

const ManageSubDepartmentPage: React.FC = () => {
  const dispatch = useDispatch();
  const [form] = useForm();
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const { screenType } = useSelector((state: RootState) => state.settings);
  const { myPermissions } = useSelector((state: RootState) => state.auth);
  const [subDepartmentId, setSubDepartmentId] = useState("");
  const [sorter, setSorter] = useState({ sort_by: "", sort_type: "" });
  const [filters, setFilters] = useState({} as any);
  // Flags
  const [createEditOpen, setCreateEditOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null as any);
  const [filterVisible, setFilterVisible] = useState(false);
  const [openLogs, setOpenLogs] = useState(false);
  const { loadingSubDepartment, subDepartmentList, pagination } = useSelector(
    (state: RootState) => state.subDepartment
  );
  const [subDepartmentListData, setSubDepartmentListData] = useState([] as any);

  useEffect(() => {
    loadList();
  }, [filters, sorter]);
  const loadList = (page: any = null) => {
    const data = {
      page: page ? page : pagination?.page ? pagination?.page : 1,
      limit: pagination?.limit || TABLE_ROWS_COUNT,
      ...sorter,
    };
    let filter = {} as any;
    Object.keys(filters)?.map((val: any) => {
      if (filters[val]) filter[val] = filters[val];
    });
    dispatch(getSubDepartmentList({ params: data, body: filter }));
  };

  const createEntityListner = () => {
    setCreateEditOpen(true);
    setSelectedEntity(null);
  };
  const editEntityListner = (entity: any) => {
    setCreateEditOpen(true);
    setSelectedEntity(entity);
  };

  const onFilterApply = (values: any) => {
    setFilters(values);
  };

  const activeFilterEntries = useMemo(() => {
    return Object.keys(filters)
      .filter((k) => isFilterValueActive(filters[k]))
      .map((key) => ({
        key,
        fieldLabel: SUBDEPT_FILTER_LABELS[key] || key,
        valueText: subDeptFilterValueText(key, filters[key]),
      }));
  }, [filters]);

  const clearAllFilters = useCallback(() => {
    setFilters({});
    form.resetFields();
  }, [form]);

  const removeFilter = useCallback(
    (key: string) => {
      setFilters((prev: any) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      const cleared =
        key in SUBDEPT_FILTER_CLEAR ? SUBDEPT_FILTER_CLEAR[key] : undefined;
      form.setFieldsValue({ [key]: cleared });
    },
    [form]
  );

  const onPageChange = (pageNumber: number) => {
    loadList(pageNumber);
  };
  useEffect(() => {
    setSubDepartmentListData(
      subDepartmentList.map((obj: any, index: number) => {
        return { key: index, ...obj };
      })
    );
  }, [subDepartmentList]);
  const hasAnySubDepartmentAccess =
  accessAllowed(myPermissions, allPermissions.UPDATE_SUBDEPARTMENT) ||
  accessAllowed(myPermissions, allPermissions.DELETE_SUBDEPARTMENT) ||
  accessAllowed(myPermissions, allPermissions.SUBDEPARTMENT_ACTIVITY_LOGS);
  const columns = [
    {
      title: "Sub Department Name",
      dataIndex: "subDepartmentName",
      sorter: false,
      key: "subDepartmentName",
    },
    {
      title: "Department",
      dataIndex: "departmentName",
      sorter: false,
      key: "departmentName",
    },
    {
      title: "Sub department owner",
      dataIndex: "subDepartmentOwner",
      sorter: false,
      key: "subDepartmentOwner",
    },
    {
      title: "Status",
      dataIndex: "status",
      sorter: false,
      key: "status",
    },
    ...(hasAnySubDepartmentAccess
      ? [
          {
            title: "Actions",
            key: "actions",
            fixed: "right",
            render: (text: any, record: any) => (
              <Space>
                {accessAllowed(
                  myPermissions,
                  allPermissions.UPDATE_SUBDEPARTMENT
                ) && (
                  <Button
                    type="link"
                    onClick={() => editEntityListner(record)}
                    icon={<EditOutlined />}
                  />
                )}
                {accessAllowed(
                  myPermissions,
                  allPermissions.DELETE_SUBDEPARTMENT
                ) && (
                  <Button
                    type="link"
                    onClick={() => handleDeleteModal(record?.id)}
                    icon={<DeleteOutlined style={{color:"#FF0000"}}  />}
                  />
                )}
                {accessAllowed(
                  myPermissions,
                  allPermissions.SUBDEPARTMENT_ACTIVITY_LOGS
                ) && (
                  <Button
                    type="link"
                    onClick={() => handleOpenLogs(record)}
                    icon={<EyeOutlined />}
                  />
                )}
              </Space>
            ),
          },
        ]
      : []),
  ];

  const handleDelete = (subDepartmentId: string) => {
    dispatch(deleteSubDepartment(subDepartmentId)).then((val: any) => {
      if (val?.payload?.data) {
        showNotification("Department Deleted Successfully", "success");
        loadList();
        setDeleteModalVisible(false); // Close the modal after successful deletion
      }
    });
  };

  const handleOpenLogs = (record: any) => {
    setOpenLogs(true);
    setSubDepartmentId(record?.id);
    setSelectedEntity(record);
  };

  const onCloseLogs = () => {
    setOpenLogs(false);
    setSubDepartmentId("");
  };

  const handleDeleteModal = (id: string) => {
    setDeleteModalVisible(true);
    setSubDepartmentId(id);
  };

  const onDeleteConfirm = () => {
    handleDelete(subDepartmentId);
  };

  const onDeleteCancel = () => {
    setDeleteModalVisible(false);
    setSubDepartmentId("");
  };
  const onValuesChange = (_value: any, _allValues: any) => {};
  const formProps = {
    form: form,
    name: "manage-sub-department-filters",
    enableEnterSubmit: true,
    formElements: [
      {
        name: "subDepartmentName",
        label: "",
        placeholder: "Sub Department Name Search",
        type: "Input",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
      },
      {
        name: "status",
        label: "",
        placeholder: "Status",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        showSearch: true,
        allowClear: true,
        mode: "multiple",
        options: [
          { value: "ACTIVE", label: "ACTIVE" },
          { value: "INACTIVE", label: "INACTIVE" },
        ],
      },
    ],
    initialValues: {
      name: "",
    },
    extraButtons: [
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Clear",
        type: "clearForm",
        onClick: clearAllFilters,
      },
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Apply",
        submitButtonType: "primary",
      },
    ],
    onFinish: onFilterApply,
    onFinishFailed: null,
    onValuesChange: onValuesChange,
  };
  const headerButtons = (
    <Space size="middle" className="app-page-actions">
      {accessAllowed(myPermissions, allPermissions.CREATE_SUBDEPARTMENT) && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={createEntityListner}
        >
          <span className="font-size-14">
            New {!screenType?.isPortrait && "Sub Department"}
          </span>
        </Button>
      )}
      <Button
        shape="circle"
        type="primary"
        icon={<BiFilterAlt />}
        onClick={() => setFilterVisible(!filterVisible)}
        title={filterVisible ? "Hide filters" : "Show filters"}
      />
    </Space>
  );
  const createEditSubmit = () => {
    setCreateEditOpen(false);
    loadList();
  };
  const onChangeHandler = (_pagination: any, _filters: any, _sorter: any) => {
    if (_sorter.order === undefined) {
      setSorter({ sort_by: "", sort_type: "" });
    } else {
      let order = "";
      let columnKey = "";
      if (_sorter.order === "ascend") order = "asc";
      else order = "desc";

      if (_sorter.columnKey === "enabled") {
        columnKey = "status";
      } else if (_sorter.columnKey === "displayName") {
        columnKey = "name";
      } else {
        columnKey = _sorter.columnKey;
      }
      setSorter({ sort_by: columnKey, sort_type: order });
    }
  };

  return (
    <div className="app-page">
      <div className="app-page-header">
        <Row align="middle" justify="space-between" wrap>
          <Col flex="1" style={{ minWidth: 0 }}>
            <div className="app-page-title-wrap">
              <div className="app-page-icon">
                <ApartmentOutlined />
              </div>
              <h1 className="app-page-title">Manage Sub Department</h1>
            </div>
            <p className="app-page-subtitle">
              View and manage sub departments
            </p>
          </Col>
          <Col>{headerButtons}</Col>
        </Row>
      </div>
      {filterVisible && (
        <div className="app-filter-bar">
          <div className="app-filter-header">
            <div className="app-filter-title">
              <BiFilterAlt style={{ fontSize: 14 }} />
              Filters
            </div>
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={() => setFilterVisible(false)}
              title="Close filters"
              aria-label="Close filters"
            />
          </div>
          <div className="table-filter-form">
            <GenericFormComponent {...formProps} />
          </div>
        </div>
      )}

      <ActiveFilterChipsBar
        show={!filterVisible}
        entries={activeFilterEntries}
        onRemove={removeFilter}
        onClearAll={clearAllFilters}
      />

      <div className="app-table-card">
        <DataTableComponent
        pagination={true}
        loading={loadingSubDepartment}
        dataSource={subDepartmentListData}
        columns={columns}
        onChange={onChangeHandler}
        defaultPage={0}
        currentPage={pagination.page}
        totalRows={pagination?.total}
        onPageChange={onPageChange}
      />
      </div>
      <Modal
        title="Confirm Delete"
        open={deleteModalVisible}
        onOk={onDeleteConfirm}
        onCancel={onDeleteCancel}
      >
        <p>Are you sure you want to delete this sub department?</p>
      </Modal>
      {createEditOpen && (
        <CreateEditSubDepartmentComponent
          onClose={() => setCreateEditOpen(false)}
          visible={createEditOpen}
          selectedEntity={selectedEntity}
          onSubmit={createEditSubmit}
        />
      )}
      {openLogs && (
        <Drawer open={openLogs} onClose={onCloseLogs} title="Log Details">
          <ActivityLogComponent
            id={selectedEntity?.id || subDepartmentId}
            record={selectedEntity}
            module="Sub Department"
          />
        </Drawer>
      )}
    </div>
  );
};

export default ManageSubDepartmentPage;
